import { useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import PhotoUploader from "@/components/base/PhotoUploader";
import { Input, Select } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { WEEKS_PER_MONTH } from "@/utils/payroll";
import { formatMoney } from "@/utils/format";
import type { AppUser, Cycle, SchoolClass, Subject, Teacher, TeacherAssignment } from "@/types";
import { cycleIdOf } from "@/utils/cycleScope";
import { EMPLOYMENT_STATUSES, EMPLOYMENT_STATUS_LABEL } from "@/utils/hr";
import type { TeacherFormState } from "./teacherForm";
import { currencySymbol } from "@/utils/currency";


/* ------------------------------------------------------------------------- */
/* Composant                                                                  */
/* ------------------------------------------------------------------------- */

interface TeacherFormModalProps {
  open: boolean;
  mode: "add" | "edit";
  form: TeacherFormState;
  onChange: (form: TeacherFormState) => void;
  onClose: () => void;
  onSubmit: (form: TeacherFormState) => void;
  cycles: Cycle[];
  classes: SchoolClass[];
  subjects: Subject[];
  /** Comptes de l'école (absents si l'utilisateur ne gère pas les comptes : le champ est alors masqué). */
  users?: AppUser[];
  /** Tous les enseignants : un compte ne peut être lié qu'à un seul. */
  teachers?: Teacher[];
  /** Enseignant modifié (son propre compte reste proposé). */
  editingId?: string;
}

export default function TeacherFormModal({
  open,
  mode,
  form,
  onChange,
  onClose,
  onSubmit,
  cycles,
  classes,
  subjects,
  users,
  teachers = [],
  editingId,
}: TeacherFormModalProps) {
  const { notify } = useToast();
  const [error, setError] = useState<string | null>(null);

  const primaryCycleId = cycleIdOf(cycles, "PRIMAIRE");
  const cycleLabels = Object.fromEntries(cycles.map((cycle) => [cycle.id, cycle.label])) as Record<string, string>;
  const primaryClasses = classes.filter((cls) => cls.cycleId === primaryCycleId);
  const secondaryClasses = classes.filter((cls) => cls.cycleId === cycleIdOf(cycles, "SECONDAIRE"));
  const subjectNameById = Object.fromEntries(subjects.map((subject) => [subject.id, subject.name])) as Record<
    string,
    string
  >;

  const isPrimary = form.cycleId === primaryCycleId;

  const fixedPay = isPrimary && form.payMode === "FIXED";
  const estimatedSalary = fixedPay
    ? Math.round(Number(form.fixedMonthlySalary) || 0)
    : Math.round((Number(form.weeklyHours) || 0) * (Number(form.hourlyRate) || 0) * WEEKS_PER_MONTH);

  const patch = (partial: Partial<TeacherFormState>) => onChange({ ...form, ...partial });

  const handleCycleChange = (cycleId: string) => {
    setError(null);
    // Changer de cycle réinitialise la classe titulaire ET les affectations.
    onChange({ ...form, cycleId, primaryClassId: "", assignments: [] });
  };

  const toggleAssignedSubject = (classId: string, subjectId: string) => {
    const existing = form.assignments.find((a) => a.classId === classId);
    let assignments: TeacherAssignment[];

    if (!existing) {
      assignments = [...form.assignments, { classId, subjectIds: [subjectId] }];
    } else if (existing.subjectIds.includes(subjectId)) {
      const remaining = existing.subjectIds.filter((id) => id !== subjectId);
      // Retirer la dernière matière d'une classe supprime l'affectation.
      assignments =
        remaining.length === 0
          ? form.assignments.filter((a) => a.classId !== classId)
          : form.assignments.map((a) => (a.classId === classId ? { ...a, subjectIds: remaining } : a));
    } else {
      assignments = form.assignments.map((a) =>
        a.classId === classId ? { ...a, subjectIds: [...a.subjectIds, subjectId] } : a,
      );
    }

    patch({ assignments });
  };

  const handleSave = () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      const msg = "Le prénom et le nom sont obligatoires.";
      setError(msg);
      notify(msg, "error");
      return;
    }
    if (fixedPay && !(Number(form.fixedMonthlySalary) > 0)) {
      const msg = "Indiquez le salaire fixe mensuel.";
      setError(msg);
      notify(msg, "error");
      return;
    }
    if (isPrimary) {
      if (!form.primaryClassId) {
        const msg = "Au primaire, une classe est obligatoire.";
        setError(msg);
        notify(msg, "error");
        return;
      }
    } else {
      const total = form.assignments.reduce((sum, a) => sum + a.subjectIds.length, 0);
      if (total === 0) {
        const msg = "Au secondaire, affectez au moins une matière à une classe.";
        setError(msg);
        notify(msg, "error");
        return;
      }
    }
    setError(null);
    onSubmit(form);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Modifier un enseignant" : "Ajouter un enseignant"}
      subtitle="Rattachez l'enseignant à un cycle"
      icon="ri-user-star-line"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={handleSave}>
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <PhotoUploader
          value={form.photo}
          onChange={(photo) => patch({ photo })}
          lastName={form.lastName}
          firstName={form.firstName}
        />

        <Select
          label="Cycle"
          required
          value={form.cycleId}
          onChange={(event) => handleCycleChange(event.target.value)}
        >
          {cycles.map((cycle) => (
            <option key={cycle.id} value={cycle.id}>
              {cycle.label}
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Prénom"
            required
            placeholder="ex. Aminata"
            value={form.firstName}
            onChange={(event) => patch({ firstName: event.target.value })}
          />
          <Input
            label="Nom"
            required
            placeholder="ex. Soumah"
            value={form.lastName}
            onChange={(event) => patch({ lastName: event.target.value })}
          />
        </div>

        <div>
          <p className="mb-2 text-xs font-medium text-foreground-700">Genre</p>
          <div className="inline-flex rounded-full border border-background-300 bg-background-100 p-1">
            {(["M", "F"] as const).map((value) => {
              const selected = form.gender === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => patch({ gender: value })}
                  className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                    selected
                      ? "bg-primary-600 text-white"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {value === "M" ? "Masculin" : "Féminin"}
                </button>
              );
            })}
          </div>
        </div>

        {isPrimary ? (
          <div>
            <Select
              label="Classe"
              required
              value={form.primaryClassId}
              onChange={(event) => patch({ primaryClassId: event.target.value })}
            >
              <option value="">Sélectionner une classe…</option>
              {primaryClasses.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.shortName} — {cls.name}
                </option>
              ))}
            </Select>
            <p className="mt-1.5 text-xs text-foreground-400">
              Au primaire, un enseignant est rattaché à une seule classe.
            </p>
          </div>
        ) : (
          <div>
            <p className="mb-2 text-xs font-medium text-foreground-700">
              Affectations (classe → matières enseignées)
            </p>
            <div className="space-y-3">
              {secondaryClasses.map((cls) => {
                const assignment = form.assignments.find((a) => a.classId === cls.id);
                return (
                  <div key={cls.id} className="rounded-lg border border-background-200 bg-background-50 p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-md bg-accent-100 px-1.5 text-xs font-semibold text-accent-800">
                        {cls.shortName}
                      </span>
                      <span className="text-xs font-medium text-foreground-800">{cls.name}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {cls.subjectIds.map((subjectId) => {
                        const selected = assignment?.subjectIds.includes(subjectId) ?? false;
                        return (
                          <button
                            key={subjectId}
                            type="button"
                            onClick={() => toggleAssignedSubject(cls.id, subjectId)}
                            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer ${
                              selected
                                ? "border-accent-500 bg-accent-500 text-accent-950"
                                : "border-background-300 bg-background-100 text-foreground-700 hover:border-accent-300 hover:text-accent-700"
                            }`}
                          >
                            {selected && (
                              <span className="flex h-3 w-3 items-center justify-center">
                                <i className="ri-check-line text-xs leading-none"></i>
                              </span>
                            )}
                            {subjectNameById[subjectId]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-foreground-400">
              Au secondaire, un enseignant donne des matières spécifiques dans des classes précises.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Téléphone"
            placeholder="+224 620 00 00 00"
            value={form.phone}
            onChange={(event) => patch({ phone: event.target.value })}
          />
          <Input
            label="Email"
            type="email"
            placeholder="prenom.nom@ecole.gn"
            value={form.email}
            onChange={(event) => patch({ email: event.target.value })}
          />
          <Select
            label="Statut"
            value={form.status}
            onChange={(event) => patch({ status: event.target.value as Teacher["status"] })}
          >
            {EMPLOYMENT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {EMPLOYMENT_STATUS_LABEL[status]}
              </option>
            ))}
          </Select>
          <Input
            label="Date d'embauche"
            type="date"
            value={form.hireDate}
            onChange={(event) => patch({ hireDate: event.target.value })}
          />
        </div>

        <div className="rounded-lg border border-background-200 bg-background-100/50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center text-primary-700">
              <i className="ri-timer-line text-base"></i>
            </span>
            <p className="text-xs font-semibold text-foreground-800">Horaires & rémunération</p>
          </div>
          {isPrimary && (
            <div className="mb-4">
              <Select
                label="Mode de paie"
                value={form.payMode}
                onChange={(event) => patch({ payMode: event.target.value as TeacherFormState["payMode"] })}
              >
                <option value="HOURLY">Taux horaire</option>
                <option value="FIXED">Salaire fixe mensuel</option>
              </Select>
            </div>
          )}
          {fixedPay ? (
            <Input
              label={`Salaire fixe mensuel (${currencySymbol()})`}
              type="number"
              min={1}
              required
              placeholder="ex. 1500000"
              value={form.fixedMonthlySalary}
              onChange={(event) => patch({ fixedMonthlySalary: event.target.value })}
            />
          ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Heures par semaine"
              type="number"
              min={0}
              placeholder="ex. 24"
              value={form.weeklyHours}
              onChange={(event) => patch({ weeklyHours: event.target.value })}
            />
            <Input
              label={`Tarif horaire (${currencySymbol()})`}
              type="number"
              min={0}
              placeholder="ex. 28000"
              value={form.hourlyRate}
              onChange={(event) => patch({ hourlyRate: event.target.value })}
            />
          </div>
          )}
          <div className="mt-3 flex items-center justify-between rounded-md border border-primary-200 bg-primary-50/70 px-3 py-2">
            <span className="text-xs text-foreground-600">{fixedPay ? "Salaire mensuel" : "Salaire mensuel estimé"}</span>
            <span className="text-sm font-semibold text-primary-900">{formatMoney(estimatedSalary)}</span>
          </div>
          <p className="mt-1.5 text-xs text-foreground-400">
            {fixedPay ? "Montant versé chaque mois, sans calcul d'heures" : `heures / semaine × tarif horaire × ${WEEKS_PER_MONTH} semaines`}
          </p>
        </div>

        {users && (
          <div className="rounded-lg border border-background-200 bg-background-100/50 p-4">
            <Select
              label="Compte de connexion (saisie des notes)"
              value={form.userId}
              onChange={(event) => patch({ userId: event.target.value })}
            >
              <option value="">Aucun compte</option>
              {users
                .filter((u) => u.isActive && !teachers.some((t) => t.userId === u.id && t.id !== editingId))
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} — {u.email} ({u.roleName})
                  </option>
                ))}
            </Select>
            <p className="mt-1.5 text-xs text-foreground-500">
              Avec ce compte, l'enseignant saisit les notes de ses seules classes et matières (rôle « Enseignant »).
            </p>
          </div>
        )}

        {error && (
          <p className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-600">
            {error}
          </p>
        )}

        <p className="text-xs text-foreground-400">
          Cycle sélectionné : {cycleLabels[form.cycleId] ?? "—"}
        </p>
      </div>
    </Modal>
  );
}